import React, { useCallback, useRef, useState } from "react";

import { CardLabel } from "./ui/Card";
import { EmailLinkPanel } from "./EmailLinkPanel";
import { EmailBodyToolbar } from "./EmailBodyToolbar";
import {
  applyFontSize,
  applyHighlight,
  applyTextColor,
  clearFormatting,
  currentFontSize,
  insertLineBreak,
  isInlineActive,
  normalizeFontTags,
  toggleInline,
} from "./emailBodyFormat";
import {
  EMAIL_LINK_STYLE,
  emailBodyPlainText,
  sanitizeEmailHtml,
} from "./emailBodyHtml";

/**
 * The email body: a small rich-text surface, and the link editor that serves it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A TEXTAREA ANY MORE
 * ---------------------------------------------------------------------------
 * The one thing an email body needs that a popup body does not is a link, and
 * the previous field asked the author to type `<a href="…">` by hand into a
 * plain textarea. That is a markup exam, and the preview showed the tags back
 * rather than the link, so there was no way to check the answer.
 *
 * It is not a full editor either — no lists, no tables, no images. What it has
 * is the formatting an email actually uses: weight, emphasis, underline, size,
 * text colour, highlight, and links. All of it written as inline styles,
 * because a mail client has no stylesheet of ours to read.
 *
 * ---------------------------------------------------------------------------
 * UNCONTROLLED, DELIBERATELY
 * ---------------------------------------------------------------------------
 * React never writes `innerHTML` after mount. A controlled `contentEditable`
 * re-renders the subtree on every keystroke, which destroys and rebuilds the
 * text node the caret lives in — the caret jumps to the start of the field and
 * typing runs backwards. So the DOM owns the markup, `onInput` reports it
 * upward, and the only writes back into the element are the surgical ones the
 * link actions make.
 *
 * The consequence to know: changing `value` from outside does not redraw the
 * body. That is fine here — the composer is create-only and nothing else edits
 * this field — and `key` on the element is the escape hatch if that changes.
 */

/**
 * NO `whitespace-pre-wrap` HERE, AND THAT IS LOAD-BEARING.
 *
 * It was there once, to make typed newlines show. It did — and it is exactly
 * why they did not show in the email. In a `contentEditable` whose computed
 * `white-space` preserves newlines, Chromium answers
 * `execCommand("insertLineBreak")` with a literal `\n` CHARACTER rather than a
 * `<br>` ELEMENT, and reports success either way. `pre-wrap` rendered that
 * character as a break in the editor and in the preview; a mail client, which
 * renders `white-space: normal`, rendered it as a space. Every line the author
 * wrote arrived as one run-on paragraph.
 *
 * Without it the browser emits real `<br>` markup, and the same rule applies to
 * the runs of spaces `contentEditable` turns into `&nbsp;`. The box now renders
 * whitespace the way the inbox will, which is the only way this field can be
 * WYSIWYG at all. `sanitizeEmailHtml` still promotes stray newlines to `<br>`
 * for anything that arrives by paste or from another browser.
 */
const BODY_BOX =
  "min-h-[190px] w-full rounded-[10px] border border-bc-border bg-bc-neut-bg " +
  "px-3.5 py-3.5 text-sm leading-[1.65] text-bc-text " +
  "focus:outline-none focus:border-bc-text transition-colors";

/**
 * The body's own base size, restated inline on the element.
 *
 * `text-sm` styles what the author sees, but the class does not travel with the
 * markup. Anything the author does not resize inherits the mail client's
 * default rather than ours, which is why the toolbar's "Normal" is a real 15px
 * span and not "unset".
 *
 * A size change with nothing selected overwrites `fontSize` on this element
 * directly. That survives re-renders because React only rewrites the style
 * properties whose PROP value changed, and this object is a module constant
 * that never changes. Make it dynamic and the imperative write starts losing.
 */
const BODY_BASE_SIZE = { fontSize: "15px" };

/**
 * The email body field.
 *
 * @param {Object} props
 * @param {string} props.value - `emailBodyHtml`. Read once, at mount — see the header note.
 * @param {(html: string) => void} props.onChange - Called with the element's markup after every edit, including the link actions.
 * @param {string} [props.errorId] - Target for `aria-describedby` when the caller is showing an error.
 * @param {boolean} [props.invalid] - Renders the field as invalid.
 * @returns {React.ReactElement}
 */
export const EmailBodyEditor = ({ value, onChange, errorId, invalid }) => {
  const bodyRef = useRef(null);
  /** The caret at the moment the panel opened, so Insert can put the link back where the author was. */
  const savedRange = useRef(null);
  /** The `<a>` being edited, when the panel was opened by clicking one. */
  const editingEl = useRef(null);
  /**
   * Mount-time markup only. Re-reading `value` here is what would fight the
   * caret. Sanitized on the way in: this is the one place the component writes
   * `innerHTML` from a value it did not itself produce.
   */
  const initialHtml = useRef(sanitizeEmailHtml(value));

  const [link, setLink] = useState({ open: false, text: "", url: "", editing: false });
  /** Drives the placeholder. Derived from the DOM, not from `value`, because the DOM is the source of truth. */
  const [empty, setEmpty] = useState(() => emailBodyPlainText(value).length === 0);
  /**
   * Bumped after anything that could move the caret, purely to re-render the
   * toolbar so its pressed states re-ask `queryCommandState`. The value is
   * meaningless; the render is the point.
   */
  const [, setCaretTick] = useState(0);
  const refreshToolbar = () => setCaretTick((tick) => tick + 1);

  /** Report the element's markup upward and refresh the placeholder. One call site for both. */
  const sync = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // Before reading it: anything pasted in as `<font>` becomes a span the
    // sanitizer will keep, so the markup reported up is the markup that ships.
    normalizeFontTags(el);
    setEmpty(emailBodyPlainText(el.innerHTML).length === 0);
    setCaretTick((tick) => tick + 1);
    onChange(el.innerHTML);
  }, [onChange]);

  /**
   * Run a formatting command and report the result.
   *
   * Focus is restored ONLY when it has actually left. Calling `focus()` on an
   * element that already has it collapses the selection to the start in some
   * browsers — which would silently turn "resize these words" into "resize
   * nothing", the exact bug this guard exists to prevent.
   */
  const format = (run) => {
    const el = bodyRef.current;
    if (el && document.activeElement !== el) el.focus();
    run();
    sync();
  };

  /**
   * Enter writes a `<br>`, not a `<div>`.
   *
   * Shift+Enter already does this; making the plain key agree keeps one body
   * from mixing two line-break mechanisms with different spacing.
   */
  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    insertLineBreak(bodyRef.current);
    sync();
  };

  /** Remember the caret before focus moves into the panel's inputs and the selection is lost. */
  const saveRange = () => {
    const selection = window.getSelection();
    const el = bodyRef.current;
    if (selection?.rangeCount && el && el.contains(selection.anchorNode)) {
      savedRange.current = selection.getRangeAt(0).cloneRange();
      return;
    }
    savedRange.current = null;
  };

  const openForNewLink = () => {
    saveRange();
    editingEl.current = null;
    setLink({
      open: true,
      editing: false,
      // Whatever was selected becomes the label — the common case is "select
      // two words, make them a link".
      text: savedRange.current ? savedRange.current.toString() : "",
      url: "",
    });
  };

  /**
   * Clicking a link inside the body edits it.
   *
   * Without this a link is write-once: `contentEditable` puts the caret inside
   * the anchor and there is no way to change where it points short of deleting
   * the words and starting again.
   */
  const handleBodyClick = (event) => {
    const anchor = event.target?.closest?.("a");
    if (anchor && bodyRef.current?.contains(anchor)) {
      event.preventDefault();
      editingEl.current = anchor;
      savedRange.current = null;
      setLink({
        open: true,
        editing: true,
        text: anchor.textContent || "",
        url: anchor.getAttribute("href") || "",
      });
      return;
    }
    saveRange();
  };

  const closePanel = () => {
    editingEl.current = null;
    setLink({ open: false, text: "", url: "", editing: false });
  };

  const submitLink = () => {
    const url = link.url.trim();
    if (!url) return;
    const label = link.text.trim() || url;

    if (link.editing && editingEl.current) {
      editingEl.current.setAttribute("href", url);
      editingEl.current.textContent = label;
    } else {
      const el = bodyRef.current;
      if (!el) return;

      const anchor = document.createElement("a");
      anchor.setAttribute("href", url);
      // Inline, because this markup is read by a mail client with no access to
      // the console's stylesheet.
      anchor.setAttribute("style", EMAIL_LINK_STYLE);
      anchor.textContent = label;

      if (savedRange.current) {
        savedRange.current.deleteContents();
        savedRange.current.insertNode(anchor);
      } else {
        // No saved caret — the author clicked Add link without ever putting
        // the cursor in the body. Appending beats dropping the link.
        el.appendChild(anchor);
      }
      // A trailing space, so the next character typed is not swallowed into
      // the anchor.
      anchor.after(document.createTextNode(" "));
    }

    sync();
    closePanel();
  };

  const removeLink = () => {
    const anchor = editingEl.current;
    if (anchor?.parentNode) {
      anchor.parentNode.replaceChild(
        document.createTextNode(anchor.textContent || ""),
        anchor
      );
      sync();
    }
    closePanel();
  };

  return (
    <div>
      <CardLabel>Body</CardLabel>

      <div className="mt-2">
        <EmailBodyToolbar
          isActive={isInlineActive}
          activeFontSize={currentFontSize(bodyRef.current)}
          onInline={(command) => format(() => toggleInline(command))}
          onFontSize={(px) => format(() => applyFontSize(bodyRef.current, px))}
          onTextColor={(value) => format(() => applyTextColor(value))}
          onHighlight={(value) => format(() => applyHighlight(value))}
          onClear={() => format(clearFormatting)}
          onAddLink={openForNewLink}
        />
      </div>

      <div className="relative mt-2">
        <div
          ref={bodyRef}
          role="textbox"
          aria-multiline="true"
          aria-label="Body"
          aria-invalid={invalid ? "true" : undefined}
          aria-describedby={errorId}
          tabIndex={0}
          contentEditable
          suppressContentEditableWarning
          data-testid="bc-email-body"
          onInput={sync}
          onBlur={sync}
          onClick={handleBodyClick}
          onKeyDown={handleKeyDown}
          // The caret moves without the markup changing, and the toolbar's
          // pressed states are a function of where it is.
          onKeyUp={refreshToolbar}
          onSelect={refreshToolbar}
          className={BODY_BOX}
          style={BODY_BASE_SIZE}
          dangerouslySetInnerHTML={{ __html: initialHtml.current }}
        />
        {/* Not a `::placeholder` — a contentEditable has none. Pointer events
            off so a click on the hint still lands in the field behind it. */}
        {empty ? (
          <div
            style={BODY_BASE_SIZE}
            className="pointer-events-none absolute left-3.5 top-3.5 leading-[1.65] text-bc-faintest"
          >
            Write the email…
          </div>
        ) : null}
      </div>

      {link.open ? (
        <EmailLinkPanel
          text={link.text}
          url={link.url}
          editing={link.editing}
          onTextChange={(text) => setLink((current) => ({ ...current, text }))}
          onUrlChange={(url) => setLink((current) => ({ ...current, url }))}
          onSubmit={submitLink}
          onRemove={removeLink}
          onCancel={closePanel}
        />
      ) : null}
    </div>
  );
};

export default EmailBodyEditor;
